import React, { useCallback, useEffect, useRef, useState } from 'react';
import Window from '../os/Window';
import useInitialWindowSize from '../../hooks/useInitialWindowSize';

function getPortfolioUrl(): string {
    const fromEnv =
        typeof process !== 'undefined' && process.env.REACT_APP_PORTFOLIO_URL?.trim();
    if (fromEnv) return fromEnv.replace(/\/?$/, '/');
    if (typeof window !== 'undefined' && window.location?.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        const host = window.location.hostname;
        if (host === 'os.filipsj.com') return 'https://portfolio.filipsj.com/';
        return `https://portfolio.${host}/`;
    }
    return 'http://127.0.0.1:3001';
}
const PORTFOLIO_URL = getPortfolioUrl();

const LOAD_TIMEOUT_MS = 12000;

const ICON_COLOR = 'rgba(255,255,255,0.8)';

function SafariIcon({
    name,
    size = 16,
    style,
}: {
    name: 'sidebar' | 'back' | 'forward' | 'lock' | 'reload' | 'share' | 'bookmark' | 'plus' | 'more' | 'globe';
    size?: number;
    style?: React.CSSProperties;
}) {
    const s = size;
    const svgStyle = { width: s, height: s, flexShrink: 0, ...style };
    switch (name) {
        case 'sidebar':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <rect x="2" y="3" width="3" height="10" rx="0.5" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" />
                    <rect x="7" y="3" width="3" height="10" rx="0.5" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" />
                </svg>
            );
        case 'back':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <path d="M10 12L6 8l4-4" fill="none" stroke={ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case 'forward':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <path d="M6 4l4 4-4 4" fill="none" stroke={ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case 'lock':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <rect x="3" y="7" width="10" height="7" rx="1" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" />
                    <path d="M5 7V5a3 3 0 016 0v2" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" />
                </svg>
            );
        case 'reload':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <path d="M14 8a6 6 0 11-2.5-4.9M14 2v4h-4" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case 'share':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <path d="M12 10v3a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h3" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 2v8M5 5l3-3 3 3" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case 'bookmark':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <path d="M4 2h8v12l-4-2.5L4 14V2z" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            );
        case 'plus':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <path d="M8 3v10M3 8h10" stroke={ICON_COLOR} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            );
        case 'more':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <circle cx="4" cy="8" r="1.2" fill={ICON_COLOR} />
                    <circle cx="8" cy="8" r="1.2" fill={ICON_COLOR} />
                    <circle cx="12" cy="8" r="1.2" fill={ICON_COLOR} />
                </svg>
            );
        case 'globe':
            return (
                <svg viewBox="0 0 16 16" style={svgStyle} aria-hidden>
                    <circle cx="8" cy="8" r="6" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" />
                    <path d="M2 8h12M8 2a10 10 0 010 12 10 10 0 010-12" fill="none" stroke={ICON_COLOR} strokeWidth="1.2" />
                </svg>
            );
        default:
            return null;
    }
}

/* Safari-style tabs: favicon + title, active = “mine” (portfolio) */
const MOCK_TABS = [
    { id: 'portfolio', label: 'Portfolio', active: true },
    { id: 'favorites', label: 'Favorites', active: false },
    { id: 'reading', label: 'Reading List', active: false },
];

export interface ShowcaseExplorerProps extends WindowAppProps {}

const ShowcaseExplorer: React.FC<ShowcaseExplorerProps> = (props) => {
    const { initWidth, initHeight } = useInitialWindowSize({ margin: 100 });
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeKey, setIframeKey] = useState(0);
    const [loadFailed, setLoadFailed] = useState(false);
    const loadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearLoadFailure = useCallback(() => {
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }
        setLoadFailed(false);
    }, []);

    const reloadIframe = useCallback(() => {
        setLoadFailed(false);
        setIframeKey((k) => k + 1);
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }
        loadTimeoutRef.current = setTimeout(() => {
            loadTimeoutRef.current = null;
            setLoadFailed(true);
        }, LOAD_TIMEOUT_MS);
    }, []);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.data?.type === 'portfolio-loaded') {
                clearLoadFailure();
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [clearLoadFailure]);

    useEffect(() => {
        loadTimeoutRef.current = setTimeout(() => {
            loadTimeoutRef.current = null;
            setLoadFailed(true);
        }, LOAD_TIMEOUT_MS);
        return () => {
            if (loadTimeoutRef.current) {
                clearTimeout(loadTimeoutRef.current);
                loadTimeoutRef.current = null;
            }
        };
    }, [iframeKey]);

    const onIframeLoad = useCallback(() => {
        clearLoadFailure();
    }, [clearLoadFailure]);

    const handleBack = useCallback(() => {
        reloadIframe();
    }, [reloadIframe]);

    return (
        <Window
            top={24}
            left={56}
            width={initWidth}
            height={initHeight}
            windowTitle="Safari"
            windowBarIcon="safariIcon"
            closeWindow={props.onClose}
            onInteract={props.onInteract}
            minimizeWindow={props.onMinimize}
            bottomLeftText={'© Filips Juzilins (Phil)'}
        >
            <div style={styles.safariWrap}>
                {/* Row 1: Safari toolbar - Sidebar, Back, Forward, URL, Reload | Share, Bookmark, New tab, More */}
                <div style={styles.toolbar}>
                    <div style={styles.toolbarLeft}>
                        <button type="button" style={styles.toolbarBtn} title="Sidebar / Reading List"><SafariIcon name="sidebar" size={18} /></button>
                        <button type="button" style={styles.navBtn} title="Back (reload)" onClick={handleBack}><SafariIcon name="back" size={18} /></button>
                        <button type="button" style={styles.navBtn} title="Forward" disabled><SafariIcon name="forward" size={18} /></button>
                        <div style={styles.addressBar}>
                            <SafariIcon name="lock" size={12} />
                            <span style={styles.urlText}>{PORTFOLIO_URL}</span>
                        </div>
                        <button type="button" style={styles.toolbarBtn} title="Reload" onClick={reloadIframe}><SafariIcon name="reload" size={16} /></button>
                    </div>
                    <div style={styles.toolbarRight}>
                        <button type="button" style={styles.toolbarBtn} title="Share"><SafariIcon name="share" size={16} /></button>
                        <button type="button" style={styles.toolbarBtn} title="Bookmarks / Reading List"><SafariIcon name="bookmark" size={16} /></button>
                        <button type="button" style={styles.toolbarBtn} title="New tab"><SafariIcon name="plus" size={16} /></button>
                        <button type="button" style={styles.toolbarBtn} title="More"><SafariIcon name="more" size={16} /></button>
                    </div>
                </div>
                {/* Row 2: Tab bar - tabs with favicon + name, open tab = Portfolio */}
                <div style={styles.tabsRow}>
                    {MOCK_TABS.map((tab) => (
                        <div
                            key={tab.id}
                            style={{
                                ...styles.tab,
                                ...(tab.active ? styles.tabActive : {}),
                            }}
                        >
                            <span style={styles.tabFavicon}><SafariIcon name="globe" size={12} /></span>
                            <span style={styles.tabLabel}>{tab.label}</span>
                        </div>
                    ))}
                    <div style={styles.tabNew}><SafariIcon name="plus" size={14} /></div>
                </div>
                {/* Page content (iframe) */}
                <div style={styles.iframeContainer}>
                    {loadFailed ? (
                        <div style={styles.failedWrap}>
                            <p style={styles.failedText}>This page didn’t load (e.g. blocked or connection refused).</p>
                            <button type="button" style={styles.retryBtn} onClick={reloadIframe}>Reload</button>
                        </div>
                    ) : (
                        <iframe
                            key={iframeKey}
                            ref={iframeRef}
                            title="Portfolio"
                            src={PORTFOLIO_URL}
                            style={styles.iframe}
                            onLoad={onIframeLoad}
                        />
                    )}
                </div>
            </div>
        </Window>
    );
};

/* Safari dark theme - toolbar on top, then tab bar (Safari layout) */
const styles: StyleSheetCSS = {
    safariWrap: {
        flex: 1,
        minHeight: 0,
        flexDirection: 'column',
        backgroundColor: '#1c1c1e',
    },
    toolbar: {
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 6,
        paddingBottom: 6,
        backgroundColor: '#2c2c2e',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    toolbarLeft: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        minWidth: 0,
    },
    toolbarRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    toolbarBtn: {
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: 6,
        backgroundColor: 'transparent',
        color: 'rgba(255,255,255,0.7)',
        fontSize: 14,
        cursor: 'pointer',
    },
    navBtn: {
        width: 28,
        height: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        borderRadius: 6,
        backgroundColor: 'transparent',
        color: 'rgba(255,255,255,0.7)',
        fontSize: 18,
        cursor: 'pointer',
    },
    addressBar: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        height: 28,
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 6,
        backgroundColor: 'rgba(60,60,67,0.6)',
        border: '1px solid rgba(255,255,255,0.1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
        fontSize: 12,
        minWidth: 0,
    },
    urlText: {
        color: 'rgba(255,255,255,0.85)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tabsRow: {
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 0,
        paddingLeft: 8,
        paddingRight: 8,
        paddingTop: 4,
        paddingBottom: 4,
        backgroundColor: '#1c1c1e',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    tab: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingTop: 6,
        paddingBottom: 6,
        paddingLeft: 12,
        paddingRight: 12,
        borderRadius: 6,
        fontSize: 12,
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif',
        color: 'rgba(255,255,255,0.5)',
        backgroundColor: 'transparent',
        cursor: 'default',
        marginRight: 2,
    },
    tabActive: {
        color: 'rgba(255,255,255,0.95)',
        fontWeight: 500,
        backgroundColor: 'rgba(255,255,255,0.08)',
    },
    tabFavicon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    tabLabel: {
        maxWidth: 100,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tabNew: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: 6,
        marginLeft: 4,
        cursor: 'default',
    },
    iframeContainer: {
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: '#1c1c1e',
        position: 'relative',
    },
    iframe: {
        width: '100%',
        height: '100%',
        border: 'none',
        display: 'block',
    },
    failedWrap: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        backgroundColor: '#1c1c1e',
        padding: 24,
    },
    failedText: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        margin: 0,
        textAlign: 'center',
    },
    retryBtn: {
        padding: '8px 16px',
        borderRadius: 8,
        border: 'none',
        backgroundColor: 'rgba(0,122,255,0.9)',
        color: '#fff',
        fontSize: 14,
        cursor: 'pointer',
    },
};

export default ShowcaseExplorer;
